// src/notes.starter.ts
// Core Starter Notes — v70 Tuned Triggers (V snapshot)
// Optimized for harmony with Trigger Matcher + Semantic Matcher
// Doctrine: 10 triggers per board, 2–4 tokens each, high-signal & distinct.
// Principles: no duplicates, conflict-free lexicons, reverse-engineered to matcher IP.

export type CueNote = {
  id: string;
  title: string;
  triggers: string[];
  body_markdown: string;
};

export const STARTER_NOTES: CueNote[] = [
  // 1) Elevator Pitch
  {
    id: "core10-elevator-pitch",
    title: "Elevator Pitch",
    triggers: [
      "about yourself",
      "introduce yourself",
      "quick intro",
      "your story",
      "who are you",
      "walk me through resume",
      "your background",
      "professional journey",
      "career story",
      "work history"
    ],
    body_markdown: [
      "- Role: Senior Marketing Manager, 8+ yrs",
      "- Highlights: 3 launches, +40% conversion",
      "- Fit: Why this role & company"
    ].join("\n")
  },

  // 2) Why This Role (covers role + company overlap)
  {
    id: "core10-role-change",
    title: "Why This Role",
    triggers: [
      "why this role",
      "why this job",
      "why join us",
      "why our company",
      "what attracted you",
      "role stand out",
      "your next opportunity",
      "right next step",
      "fit for this role",
      "why this team"
    ],
    body_markdown: [
      "- Why this role (skills match)",
      "- Why this company/team",
      "- What you’ll deliver in 90 days"
    ].join("\n")
  },

  // 3) Strengths
  {
    id: "core10-strengths",
    title: "Strengths",
    triggers: [
      "your strengths",
      "what you do best",
      "really good at",
      "what sets you apart",
      "superpower",
      "known for",
      "go to person",
      "people rely on you",
      "strongest skill",
      "especially proud of"
    ],
    body_markdown: [
      "- Strength 1 + proof",
      "- Strength 2 + proof",
      "- Ties back to role needs"
    ].join("\n")
  },

  // 4) Weaknesses
  {
    id: "core10-weaknesses",
    title: "Weaknesses",
    triggers: [
      "your weaknesses",
      "biggest weakness",
      "areas to improve",
      "areas for development",
      "skill gap",
      "growth areas",
      "toughest feedback",
      "respond to feedback",
      "still learning",
      "not great at yet"
    ],
    body_markdown: [
      "- Real area (not core to role)",
      "- Steps taken to improve",
      "- Guardrail to mitigate risk"
    ].join("\n")
  },

  // 5) STAR — Roadblock (failure / obstacles only)
  {
    id: "core10-star-roadblock",
    title: "STAR — Roadblock",
    triggers: [
      "went wrong",
      "went sideways",
      "setback",
      "didn't go as planned",
      "time you failed",
      "unexpected issue",
      "escalation",
      "major challenge",
      "retrospective",
      "firefighting"
    ],
    body_markdown: ["- Situation", "- Task", "- Action", "- Result"].join("\n")
  },

  // 6) STAR — Leadership
  {
    id: "core10-star-leadership",
    title: "STAR — Leadership",
    triggers: [
      "led a team",
      "managed people",
      "leadership example",
      "influenced without authority",
      "developed talent",
      "scaled a team",
      "mentored someone",
      "built a team",
      "set direction",
      "drove change"
    ],
    body_markdown: ["- Situation", "- Task", "- Action", "- Result"].join("\n")
  },

  // 7) Career Goals
  {
    id: "core10-career-goals",
    title: "Career Goals",
    triggers: [
      "career",
      "professional goals",
      "aiming professionally",
      "next couple of years",
      "one to three years",
      "next 12 months",
      "near-term goals",
      "where you see yourself",
      "long term goals",
      "future plans"
    ],
    body_markdown: [
      "- 12–24 month focus",
      "- Skills to deepen",
      "- How this role ladders up"
    ].join("\n")
  },

  // 8) Project Management (execution / coordination only)
  {
    id: "core10-project-management",
    title: "Project Management",
    triggers: [
      "project management",
      "keep things moving",
      "prioritize work",
      "timeline management",
      "delivery plan",
      "organize projects",
      "manage stakeholders",
      "keep teams aligned",
      "status updates",
      "risk management"
    ],
    body_markdown: [
      "- Planning (scope, timelines)",
      "- Execution (ownership, comms)",
      "- Review (retros, metrics)"
    ].join("\n")
  },

  // 9) Metrics & Success
  {
    id: "core10-metrics",
    title: "Metrics & Success",
    triggers: [
      "key metrics",
      "measure success",
      "KPIs",
      "performance metrics",
      "success criteria",
      "measure impact",
      "reporting cadence",
      "north star metric",
      "leading indicators",
      "metrics"
    ],
    body_markdown: [
      "- North-star",
      "- Leading indicators",
      "- Instrumentation & cadence"
    ].join("\n")
  },

  // 10) Salary & Compensation
  {
    id: "core10-salary",
    title: "Salary & Compensation",
    triggers: [
      "salary expectations",
      "salary",
      "total compensation",
      "salary range",
      "desired pay",
      "comp expectations",
      "compensation expectations",
      "pay expectations",
      "compensation",
      "expected pay"
    ],
    body_markdown: [
      "- Research-backed range",
      "- Total comp (base + bonus + equity)",
      "- Signals flexibility"
    ].join("\n")
  },

  // 11) Handling Conflict
  {
    id: "core12-conflict",
    title: "Handling Conflict",
    triggers: [
      "team conflict",
      "disagreed with someone",
      "conflict on team",
      "resolved a disagreement",
      "handled conflict",
      "difficult teammate",
      "navigated disagreement",
      "tough conversation",
      "conflict resolution",
      "disagreement with colleague"
    ],
    body_markdown: [
      "- Context & stakes (who, what, why it mattered)",
      "- Approach (empathy, shared goals, options considered)",
      "- Actions (1–2 concrete steps; data/ADR if relevant)",
      "- Outcome & learning (relationship + result)"
    ].join("\n")
  },

  // 12) Questions for the Interviewer
  {
    id: "core12-questions",
    title: "Questions",
    triggers: [
      "any questions for me",
      "questions for us",
      "questions about the role",
      "questions about company",
      "anything to ask",
      "closing questions",
      "topics to ask",
      "clarify next steps",
      "questions you have",
      "interview questions"
    ],
    body_markdown: [
      "- Team & role: expectations, success in 90 days",
      "- Metrics & impact: what ‘great’ looks like",
      "- Collaboration & culture: ways of working",
      "- Product & roadmap: priorities, next steps"
    ].join("\n")
  }
];

export default STARTER_NOTES;
